import type { FC } from 'react'

import { cn } from '@/utils'
import { addSeconds, isAfter, isBefore } from 'date-fns'

import { DimmedDuration } from '@/shared/ui/dimmed-duration'

import { useRundownPageSnap } from '../../rundown-page.store'

import { useGroupTiming, useGroupTimestamps } from './group.hooks'
import { getGroupRunTimingView } from './group.timing'
import type { GroupChildren } from './group.types'
import { GroupRange } from './GroupRange'
import { GroupTimingsRow } from './GroupTimingsRow'

type GroupRunTimingsProps = {
  childrenCues: GroupChildren
}

/**
 * Live timing for a group: the countdown while it runs, or the actual duration
 * once it has finished or drifted from plan.
 */
export const GroupRunTimings: FC<GroupRunTimingsProps> = ({ childrenCues }) => {
  const { rundown } = useRundownPageSnap()
  const groupTimestamps = useGroupTimestamps(childrenCues)
  const groupTiming = useGroupTiming(groupTimestamps)

  if (!groupTiming) return null

  const view = getGroupRunTimingView(groupTiming)
  if (!view) return null

  return (
    <GroupTimingsRow
      className="bg-gray-900 py-0.5"
      Duration={
        <DimmedDuration
          className={cn(
            'text-sm opacity-50',
            view.overdue && 'text-red-600 opacity-100',
          )}
          ms={view.ms}
          roundingFn={view.kind === 'elapsed' ? Math.round : undefined}
        />
      }
      Range={
        <GroupRange
          start={groupTiming.actual.start}
          end={groupTiming.actual.end}
          timezone={rundown.timezone}
          todDisplayFormat={rundown.settings?.todDisplayFormat}
          separatorClassName={
            view.kind === 'countdown' ? 'opacity-50' : undefined
          }
          startClassName={rangeClassNames(
            groupTiming.actual.start,
            groupTiming.original.start,
            view.toleranceSeconds,
          )}
          endClassName={rangeClassNames(
            groupTiming.actual.end,
            groupTiming.original.end,
            view.toleranceSeconds,
          )}
        />
      }
    />
  )
}

function rangeClassNames(
  actual: Date,
  original: Date,
  toleranceSeconds: number,
): string {
  const lateAfter = toleranceSeconds
    ? addSeconds(original, toleranceSeconds)
    : original

  return cn(
    'opacity-50',
    isBefore(actual, original) && 'text-green-600 opacity-100',
    isAfter(actual, lateAfter) && 'text-red-600 opacity-100',
  )
}
